import React, { createContext, useContext, useEffect, useState } from 'react';
import { API } from 'utilities/api';
import { isPlatformHost } from 'config';
import { SiteHeader, SiteFooter } from './SiteFrame';
import NotFoundBody from './NotFoundBody';

/**
 * Hides the shop from brands that do not have one.
 *
 * Every storefront route — /shop, /cart, /checkout, /wishlist, /track — is
 * registered for every brand, so a brand that only ever built pages was still
 * serving a working checkout at its own domain. A visitor could reach a cart
 * for a business that sells nothing.
 *
 * Wrapping the routes rather than patching each page: there are eight of
 * them, and a guard that has to be remembered per page is one that will be
 * forgotten on the ninth — which is exactly what happened to /contact, the
 * one storefront path every brand's menu links to.
 *
 * While the check is in flight it renders nothing rather than either answer.
 * Flashing a shop that then disappears is worse than a blank moment, and
 * flashing "not found" at a brand that does have a shop is worse still.
 */
/**
 * The brand's global frame, shared with storefront pages.
 *
 * The gate already fetches it to render its own not-found screen, so passing
 * it down costs nothing — and without it the storefront was an island: its
 * own nav, none of the brand's menu, no footer, no way back to the pages the
 * owner built.
 */
const FrameContext = createContext(null);

export const useStorefrontFrame = () => useContext(FrameContext);

export default function StorefrontGate({ children, fallback = null }) {
  const [state, setState] = useState({ phase: 'checking', frame: null, brand: null });

  useEffect(() => {
    let cancelled = false;

    // On the platform host these paths are previews driven by :brandRef in
    // the URL, not a brand's own storefront, so the gate does not apply.
    if (isPlatformHost()) { setState({ phase: 'allow' }); return undefined; }

    API.publicSiteFrame({})
      .then((res) => {
        if (cancelled) return;
        setState({
          // Absent rather than false means an older backend that does not
          // report this yet — allow, rather than hiding every shop on the
          // platform the moment the frontend deploys first.
          phase: res?.has_shop === false ? 'block' : 'allow',
          frame: res?.global || null,
          brand: res?.brand || null,
        });
      })
      .catch(() => { if (!cancelled) setState({ phase: 'allow' }); });

    return () => { cancelled = true; };
  }, []);

  if (state.phase === 'checking') return null;

  if (state.phase === 'allow') {
    const f = state.frame || {};
    return (
      <FrameContext.Provider value={{ frame: f, brand: state.brand }}>
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#ffffff' }}>
          <div style={{ flex: 1 }}>{children}</div>
          {/* The brand's own footer under the shop, so a customer who wandered
              in from the homepage still has the site's navigation. */}
          <SiteFooter footer={f.footer} brandName={state.brand?.name} />
        </div>
      </FrameContext.Provider>
    );
  }

  // Some of these paths are ones a brand without a shop plausibly owns as a
  // real page — /contact most of all. Answering "not found" there would be
  // wrong twice over: the address is reserved by the storefront, so the
  // owner's own contact page could never be reached at it, and the visitor
  // gets a dead end at the one URL every business is expected to have.
  //
  // So a blocked route can hand off rather than refuse. If the owner has no
  // page at that address either, whatever we hand off to says so itself.
  if (fallback) return fallback;

  const frame = state.frame || {};
  const brandName = state.brand?.name;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#ffffff' }}>
      <SiteHeader
        header={frame.header}
        brandName={brandName}
        brandLogo={state.brand?.logo_upload_path}
      />
      <NotFoundBody notFound={frame.notFound} />
      <SiteFooter footer={frame.footer} brandName={brandName} />
    </div>
  );
}
